import { buildCollection, buildProperty, EntityAction } from "@firecms/core";

import {
  hasAnyRole,
  ONBOARDING_QUEUE_ROLES,
  ONBOARDING_VIEW_ROLES,
} from "./apple_delegated_onboarding.roles";
import {
  GALLERY_PHOTO_SOURCE_LABELS,
  ONBOARDING_GALLERY_PHOTO_LABEL,
  ONBOARDING_GALLERY_PHOTOS_PATH,
  OnboardingGalleryPhotoEntity,
} from "./apple_delegated_onboarding.types";
import {
  evaluateQueueGate,
  resolveDocumentEnvironment,
  stripFieldsTheFormMayNotWrite,
} from "./apple_delegated_onboarding.utils";
import { fetchOnboardingParent, queueOnboardingStep } from "./apple_delegated_onboarding.write";
import { stepStatusProperties } from "./apple_delegated_onboarding_properties";
import { AppleImageCropFieldProps } from "./apple_image_crop.types";
import { AppleImageCropField } from "./AppleImageCropField";
import { GalleryPhotoChip } from "./OnboardingStepChip";
import { OnboardingGalleryPhotoView } from "./OnboardingStepsView";

/**
 * `gallery_photos` — the practice's photo gallery on its Apple place card, one document per photo.
 *
 * ## ⚠️ THE SECOND SUBCOLLECTION, AND THE SHAPE IS WHAT MAKES THIS SURFACE SIMPLE
 *
 * A location may hold up to ONE HUNDRED gallery photos, so the obvious CMS shape is an ARRAY of image
 * inputs on an embedded `galleryPhotos` step. That is the wrong shape, and three separate things say so:
 *
 *  1. **Firestore cannot merge-write one array element.** The whole field-ownership contract
 *     (`ONBOARDING_STEP_FORM_OWNED_FIELDS` here, `ONBOARDING_STEP_CMS_OWNED_FIELDS` on the backend)
 *     rests on each writer touching only its OWN field paths. Writing photo 37's Apple result would
 *     mean read-modify-writing the entire array and clobbering a staff edit to photo 12.
 *  2. **`pruneWhitelistedInPlace`'s `deleteField()` sentinel is scalar-only.** An `undefined` assigned
 *     to a map- or array-typed property gets walked by FireCMS's `updateDateAutoValues` and written as
 *     literal `{ _methodName: "deleteField" }` garbage. An array of maps is exactly the shape that trips
 *     it.
 *  3. **No array in this console puts a custom `Field:` on its `of:` map.** It would be the first, and
 *     `AppleImageCropField` holds its in-flight `ImageBitmap` in a single-slot ref — one instance, one
 *     photo.
 *
 * As a subcollection, all three problems simply do not arise, and the "list of up to 100" the ticket
 * asks for is what FireCMS renders for a subcollection anyway: a table with add and delete, one row per
 * photo. So staff get add / caption / remove with no custom list code at all.
 *
 * ⚠️ AND THERE IS NO REORDER CONTROL, DELIBERATELY. Nothing in Apple's location-asset contract lets a
 * partner set gallery ORDER — there is no rank or position field to send — so a reorder affordance here
 * would be a control that appears to do something and does not. Photos are one set, not a sequence.
 *
 * ## ⚠️ ITS STATUS FIELDS SIT AT THE DOCUMENT ROOT
 *
 * `submitState`, not `galleryPhoto.submitState` — one photo per document, so there is nothing to nest
 * under. The same convention `showcase_requests` follows, and the reason every helper in that surface
 * takes a step name of `null`.
 *
 * ## ⚠️ THE DOCUMENT ID IS THE PHOTO'S IDENTITY — DO NOT ADD `customId`
 *
 * The backend derives the photo's Apple resolve key (`partnersAssetId`) from THIS document's id, so the
 * id must be stable for the life of the photo and must never be typed by a human. FireCMS mints it on
 * row creation and Firestore guarantees it is unique. Turning on `customId` here would hand staff a
 * field that silently re-points a photo at a different Apple asset.
 *
 * ⚠️ THE CONSEQUENCE STAFF WILL HIT, and the description below says it too: DELETING A ROW HERE DOES NOT
 * REMOVE THE PHOTO FROM APPLE. A new row is a new document id, so it becomes a NEW Apple asset while the
 * old one keeps serving. Removing a photo from the place card is a delete against Apple.
 *
 * ## ⚠️ "NOT WIRED YET" IS WITHDRAWN — THIS SURFACE IS NOW REGISTERED AND QUEUEABLE
 *
 * The header and the foot of this file both used to say that nothing here could be queued, and that
 * staff could fill the form to no effect. Both claims are now FALSE and are recorded as withdrawn
 * rather than deleted, so a reader who remembers them can see what changed and why:
 *
 *  - the subcollection IS registered on `apple_delegated_onboarding` (its `subcollections` array), so
 *    it appears under an onboarding record;
 *  - there IS a Queue tab (`OnboardingGalleryPhotoView`), a row action (below) and a status chip
 *    (`GalleryPhotoChip`), all of which route through the ONE `evaluateQueueGate`;
 *  - the reason for withholding it — that staff would enter photos which went nowhere — is gone,
 *    because the backend drain that consumes these documents lands in the same wave.
 *
 * The gate's target (`"galleryPhoto"`) and its resource noun now live where every other target's do:
 * `QueueTarget` in `apple_delegated_onboarding.utils.ts`, derived from `ONBOARDING_STEP_RESOURCES` in
 * `apple_delegated_onboarding.types.ts`. No second, local gate was written — see this file's foot.
 *
 * ⚠️ The path constant, the staff label, the source vocabulary and the entity type ALSO moved out of
 * this file into `apple_delegated_onboarding.types.ts`, where their siblings live. The old docblock
 * said they were declared locally only because that lane did not own that file, and that they should
 * be moved "when this lands". This is that landing.
 */

/**
 * Row action mirroring the gated button on the photo's "Queue" tab, for the list inside the parent
 * entity where that tab is not on screen.
 *
 * ⚠️ `EntityAction` in FireCMS 3.0.0-beta.14 has no `isEnabled`/`disabled` predicate (documented at
 * `apple_grant_status.ts:28-31`), so the gate runs INSIDE `onClick` and explains itself in a snackbar
 * rather than the action being absent. It calls the SAME `evaluateQueueGate` the Queue tab calls, so a
 * photo can never be refused on one surface and allowed on the other.
 */
const queueGalleryPhotoAction: EntityAction<Partial<OnboardingGalleryPhotoEntity>> = {
  name: `Queue ${ONBOARDING_GALLERY_PHOTO_LABEL.toLowerCase()}`,
  key: "queue_gallery_photo",
  onClick: async ({ entity, context }) => {
    const { snackbarController, authController, dataSource } = context;

    if (!hasAnyRole(authController, ONBOARDING_QUEUE_ROLES)) {
      snackbarController.open({
        type: "error",
        message: "You do not have permission to queue Apple onboarding steps.",
      });
      return;
    }

    // Apple's location id — the thing a gallery photo hangs off — lives on the PARENT document, which
    // FireCMS does not hand a subcollection. A failed read degrades the gate to "the location was not
    // checked here" rather than refusing; the backend re-checks it authoritatively before Apple.
    const parent = await fetchOnboardingParent(dataSource, entity.path);
    const gate = evaluateQueueGate({
      target: "galleryPhoto",
      step: entity.values,
      parent,
      env: resolveDocumentEnvironment(parent),
    });

    if (!gate.allowed) {
      snackbarController.open({ type: "warning", message: gate.reason });
      return;
    }
    for (const caution of gate.cautions) {
      snackbarController.open({ type: "info", message: caution });
    }

    await queueOnboardingStep(
      { dataSource, snackbarController },
      { path: entity.path, entityId: entity.id, queueTarget: "galleryPhoto" },
    );
  },
};

export const appleOnboardingGalleryPhotosCollection = buildCollection<
  Partial<OnboardingGalleryPhotoEntity>
>({
  id: ONBOARDING_GALLERY_PHOTOS_PATH,
  path: ONBOARDING_GALLERY_PHOTOS_PATH,
  name: "Gallery photos",
  singularName: ONBOARDING_GALLERY_PHOTO_LABEL,
  description:
    "Photos for this location's Apple place card — one row per photo, up to 100 per location. Attach a photo, frame the crop and write a caption for each, then send it with the Queue tab on that photo. ⚠️ Deleting a row here does NOT remove the photo from Apple: a new row becomes a NEW Apple photo while the old one keeps showing. Ask an engineer to delete it at Apple.",
  icon: "photo_library",
  // ⚠️ NO `customId`. The document id IS the photo's Apple resolve key — see the file header.
  permissions: ({ authController }) => ({
    read: hasAnyRole(authController, ONBOARDING_VIEW_ROLES),
    edit: hasAnyRole(authController, ONBOARDING_VIEW_ROLES),
    create: hasAnyRole(authController, ONBOARDING_VIEW_ROLES),
    // Deleting a row does NOT delete anything at Apple. Restricted to the same roles that may queue, so
    // the audit trail for a photo that DID reach Apple is not casually removed — and so the misleading
    // action is behind the narrower role.
    delete: hasAnyRole(authController, ONBOARDING_QUEUE_ROLES),
  }),
  entityActions: [queueGalleryPhotoAction],
  entityViews: [
    {
      key: "queue_gallery_photo",
      name: "Queue",
      position: "start",
      Builder: OnboardingGalleryPhotoView,
    },
  ],
  callbacks: {
    // The whole write boundary for the form, in one line. Applies unchanged here: this document's status
    // fields are at its root, which is the shape `stripFieldsTheFormMayNotWrite` already handles for the
    // showcase subcollection.
    onPreSave: ({ values }) => stripFieldsTheFormMayNotWrite(values),
  },
  // ⚠️ Must list EVERY property key: `getFormFieldKeys` filters the entity form through this same array,
  // so an omitted key disappears from the DETAIL FORM too, not only from the table.
  propertiesOrder: [
    // The status chip's `additionalFields` key, first so it leads the table instead of being appended
    // after every property — same reasoning as the showcase and grant-status collections.
    "status",
    "input",
    "companyId",
    "submitState",
    "queuedAt",
    "attempts",
    "failureCode",
    "failureMessage",
    "writeError",
    "claimedAt",
    "completedAt",
    "apple",
  ],
  properties: {
    input: buildProperty({
      name: "Photo",
      description:
        "The photo and its caption. Apple reviews gallery photos and takes a few business days; a rejection appears in the failure reason below.",
      dataType: "map",
      properties: {
        photo: buildProperty({
          name: "Image",
          description:
            "Between 100 and 4864 pixels per side, and any shape between 1:2.2 (tall) and 2.2:1 (wide) — a much wider window than the cover photo's, so most photographs fit. Attach a JPEG or PNG, pick a shape, and frame the crop. ⚠️ No HEIC: this console cannot convert one, so export it to JPEG first — renaming the file does not convert it. No SVG (a photograph is not a vector). The backend re-checks the geometry from the file's own header before any Apple call.",
          dataType: "map",
          // ⚠️ THE SAME CROPPER, on its one surface whose Apple rule is a RANGE rather than a single
          // ratio — so this is the only surface that renders a shape picker. See
          // `apple_image_crop.utils.ts` for the presets and why none of them sits on Apple's boundary.
          //
          // ⚠️ AND IT IS ATTACHED TO THIS NESTED MAP, NOT TO `input`. The field calls
          // `setValue({ fileName, imageUrl })`, replacing the map it is bound to WHOLESALE — bound to
          // `input` it would wipe `captions` and `source` on every crop. Same nesting, same reason, as
          // `brandAssets.input.coverPhoto`.
          Field: AppleImageCropField,
          customProps: { surface: "galleryPhoto" } satisfies AppleImageCropFieldProps,
          properties: {
            fileName: buildProperty({
              name: "File name",
              description: "What Apple is told the file is called. Required.",
              dataType: "string",
            }),
            imageUrl: buildProperty({
              name: "Image URL",
              description:
                "A DearDoc storage URL the backend downloads before uploading. Required. ⚠️ Deliberately NOT validated as a URL here: a bad value must surface as an actionable GALLERY_PHOTO_IMAGE_UNUSABLE failure with a message, not as a document the drain silently skips.",
              dataType: "string",
            }),
          },
        }),
        captions: buildProperty({
          name: "Captions",
          description:
            "At least one is required — Apple takes a caption per locale. ⚠️ Every row needs BOTH a title and alt text; a half-filled row rejects the whole photo, so remove a row rather than leaving it blank. Alt text is what a screen-reader user hears in place of the photo, so describe the subject — Apple's guidance is NOT to write “photo of…”, since the reader already knows it is an image.",
          dataType: "array",
          of: {
            dataType: "map",
            properties: {
              title: buildProperty({
                name: "Title",
                description: "Short label for the photo, e.g. “Reception”.",
                dataType: "string",
              }),
              altText: buildProperty({
                name: "Alt text",
                description:
                  "What a screen reader announces. Describe the subject concisely; do not start with “photo of”.",
                dataType: "string",
              }),
              locale: buildProperty({
                name: "Locale",
                description: "Optional, e.g. en-US. Defaults to en-US if left empty.",
                dataType: "string",
              }),
            },
          },
        }),
        source: buildProperty({
          name: "Who supplied the photo",
          description:
            "Apple records who the photo came from. Leave this as the business unless the photo genuinely came from a customer or an agency.",
          dataType: "string",
          defaultValue: "BUSINESS",
          enumValues: GALLERY_PHOTO_SOURCE_LABELS,
        }),
      },
    }),
    ...stepStatusProperties({ resourceNoun: "gallery photo" }),
  },
  additionalFields: [
    {
      key: "status",
      name: "Status",
      dependencies: ["submitState", "apple"],
      Builder: GalleryPhotoChip,
    },
  ],
});

/*
 * ⚠️ THE "WHAT IS DELIBERATELY MISSING" BLOCK THAT USED TO CLOSE THIS FILE IS WITHDRAWN, AND HOW IT
 * WAS CLOSED OUT IS THE PART WORTH KEEPING.
 *
 * It said this surface had no "Queue photo" entity action, no Queue entity view and no status chip,
 * because supplying them meant editing files that lane did not own — and that writing a SECOND, local
 * gate instead "would be the drift defect this codebase keeps having to fix". That refusal was right,
 * and the fix was to widen the single definition rather than to work around it:
 *
 *  - `QueueTarget` in `apple_delegated_onboarding.utils.ts` is now `keyof typeof
 *    ONBOARDING_STEP_RESOURCES`, so the queue targets ARE the resource table's keys. `"galleryPhoto"`
 *    was added to that table (`apple_delegated_onboarding.types.ts`) with the noun "gallery photo" and
 *    `mintsResource: true` — which agrees with the backend, whose own entry for this step is
 *    `reissueHazard: 'DUPLICATE'`. (The backend's field is a three-value enum rather than a boolean,
 *    because a boolean cannot describe a destructive action; this table stays a boolean because no
 *    target in it is destructive. See that table's header.)
 *  - Because the union is derived, `evaluatePerStepGate`'s switch would not compile without a
 *    `"galleryPhoto"` branch — so `evaluateGalleryPhotoGate` is the ONLY gate for this surface, and
 *    both the Queue tab and the row action above call it through `evaluateQueueGate`.
 *  - The field-path and label mappings that used to be inlined per surface (`target === "showcase" ?
 *    null : target`, and a label derived from `step === null`) are now `stepFieldOf` and
 *    `queueTargetLabel`. With two root-status subcollections the old inline versions would have been
 *    actively wrong, not merely duplicated.
 *
 * ⚠️ STILL DELIBERATELY ABSENT, and NOT for want of a home to put it in:
 *
 *  - NO REORDER CONTROL. Nothing in Apple's location-asset contract lets a partner set gallery ORDER,
 *    so the affordance would appear to do something and would not. See the file header.
 *  - NO CROP OR HEIC AUTOMATION. This console cannot convert a HEIC and does not try; the photo
 *    property's description tells staff to export to JPEG, and the backend re-checks the file's own
 *    header before any Apple call.
 *  - NO SECOND COVER-PHOTO PATH. The cover photo is the brand-assets step; a gallery photo is a
 *    LOCATION asset. They are different Apple resources with different rules, and the cropper's
 *    `surface: "galleryPhoto"` is what keeps the two sets of geometry rules apart.
 */
